import { get_attribute_label, get_attribute_name, get_attribute_value } from '/srv/web/wyvr/html/releases/58819/gen/client/shop/core/attributes.js?1733898722647';

/**
 * Checks if an attribute exists in a product.
 *
 * @param {Object} product - The product to check.
 * @param {(string|Object[]|Object)} attribute - The attribute(s) to look for.
 * @return {boolean} True if the attribute exists, otherwise false.
 */
export function attribute_exists(product, attribute) {
    if (!product || !attribute) {
        return false;
    }
    if (typeof attribute == 'string') {
        return !!get_attribute_value(product, attribute);
    }
    if (Array.isArray(attribute)) {
        return !!attribute.find((attr) => !!get_attribute_value(product, attr));
    }
    if (typeof attribute == 'object') {
        let attr = attribute?.attribute;
        if (!attr) {
            attr = attribute?.fallback;
        }
        return attribute_exists(product, attr);
    }

    console.log('not implemented attribute: ' + attribute);
    return false;
}

/**
 * Gets the label of the given attribute from a product.
 *
 * @param {Object} product - The product object.
 * @param {(string|Object)} attribute - The attribute whose label to retrieve.
 * @return {string} The attribute's label.
 */
export function get_label(product, attribute) {
    if (typeof attribute == 'string') {
        return get_attribute_label(product, attribute);
    }
    if (typeof attribute == 'object') {
        if (typeof attribute.name == 'string') {
            return window.__(attribute.name);
        }
        if (typeof attribute.attribute == 'string') {
            return get_attribute_label(product, attribute.attribute);
        }
    }
    return JSON.stringify(attribute);
}

/**
 * Gets the value of the given attribute from a product.
 *
 * @param {Object} product - The product object.
 * @param {Object} attribute - The attribute object with 'attribute' and optional 'fallback' properties.
 * @return {any} The value of the attribute or its fallback.
 */
export function get_value(product, attribute) {
    const value = get_attribute_name(product, attribute.attribute);
    if (!attribute.fallback || typeof value != 'object') {
        return value;
    }
    return get_attribute_name(product, attribute.fallback);
}

/**
 * Generates an array of attribute names from a product.
 *
 * @param {Object} product - The product object.
 * @param {Object[]} attributes - The array of attributes to retrieve.
 * @return {string[]} An array of attribute names. Undefined attributes are not included.
 */
export function get_list(product, attributes) {
    if (!Array.isArray(attributes)) {
        return undefined;
    }
    return attributes
        .flatMap((attribute) => {
            const text = get_attribute_name(product, attribute);
            if (!text) {
                return undefined;
            }
            return text.split(', ');
        })
        .filter(Boolean);
}

/**
 * Converts a value to a specified type (currently only supports 'number').
 *
 * @param {any} value - The value to transform.
 * @param {string} to - The target type ('number' supported).
 * @param {string} [locale='en'] - The locale for number formatting.
 * @param {number} [digits=2] - The maximum significant digits for number formatting.
 * @return {any} The transformed value, or the original value if the transformation is unsupported.
 */
export function transform_to(value, to, locale = 'en', digits = 2) {
    if (!to) {
        return value;
    }
    if (!locale) {
        locale = 'en';
    }
    if (digits === null) {
        digits = 2;
    }
    switch (to) {
        case 'number': {
            // @see https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat
            const formatter = new Intl.NumberFormat(locale, {
                maximumSignificantDigits: digits,
                style: 'decimal'
            });
            return formatter.format(parseFloat(value));
        }
    }
    return value;
}
